// Manual web-push subscription helpers — bypasses the OneSignal Web
// SDK because its optIn() flow hangs indefinitely on Android PWA
// without ever throwing or logging. We do the browser-native work
// (SW register + PushManager.subscribe) ourselves and hand the
// resulting endpoint+keys to our own /api/push/subscribe route, which
// forwards to OneSignal's REST API.
//
// Why direct PushManager: we proved in diagnostics that:
//   - navigator.serviceWorker.register('/push/...') works
//   - Notification.requestPermission() returns 'granted' in gesture
//   - The browser supports PushManager
//   - Only the OneSignal SDK's internal handshake was failing
//
// VAPID public key comes from OneSignal's app config at:
//   https://onesignal.com/api/v1/sync/<app_id>/web → vapid_public_key
// It's a per-app constant, safe to embed in the bundle.

import api from '../services/api';

// Our own VAPID public key, baked at build time via REACT_APP_VAPID_PUBLIC_KEY.
// Hardcoded fallback so local dev (without the env var set) still works.
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY
  || 'BC-5uWat2yNJo7h192Mnongc4YW5HLSwJ3kAC1cs55qy2YJADKqAbKxym7m3KYvyvhBQ760PO7hCpqJ9Mfa9QP4';
const WORKER_PATH = '/push/avayble-push.js';
const WORKER_SCOPE = '/push/';

function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function bufferToBase64(buf) {
  if (!buf) return '';
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.byteLength; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// Register the OneSignal worker file at the /push/ scope and wait for
// it to become active. Returns the ServiceWorkerRegistration so the
// caller can chain subscribe().
async function registerWorker() {
  if (!('serviceWorker' in navigator)) throw new Error('Service workers not supported');
  // Clear any stale SW registered at the legacy OneSignal path —
  // those are bound to OneSignal's CDN-imported handler and silently
  // drop pushes from our web-push library because the payload shape
  // doesn't match what OneSignal's SW expects.
  try {
    const legacy = await navigator.serviceWorker.getRegistration('/push/OneSignalSDKWorker.js');
    if (legacy && !legacy.active?.scriptURL?.endsWith('avayble-push.js')) {
      await legacy.unregister();
    }
  } catch (_) { /* best-effort */ }
  const reg = await navigator.serviceWorker.register(WORKER_PATH, { scope: WORKER_SCOPE });
  if (reg.active) return reg;
  // Wait for the SW to activate (max 8s) so PushManager has a live
  // worker to attach the subscription to.
  await new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('Worker activation timed out')), 8000);
    const onState = () => {
      const w = reg.installing || reg.waiting || reg.active;
      if (w?.state === 'activated') { clearTimeout(t); resolve(); }
      else if (w) w.addEventListener('statechange', onState);
    };
    onState();
  });
  return reg;
}

// Full subscribe flow: gesture-context permission → register SW →
// PushManager.subscribe → forward to /api/push/subscribe.
//
// Must be called from a user-gesture handler — Chrome on Android
// requires Notification.requestPermission to be synchronous off the
// click. The rest is async-safe after permission resolves.
export async function subscribeToPush() {
  if (typeof Notification === 'undefined') {
    return { ok: false, reason: 'notifications_unsupported' };
  }
  let perm = Notification.permission;
  if (perm === 'default') {
    perm = await Notification.requestPermission();
  }
  if (perm !== 'granted') {
    return { ok: false, reason: perm === 'denied' ? 'permission_denied' : 'permission_dismissed' };
  }

  const reg = await registerWorker();
  // Reuse any existing subscription so we don't churn endpoints on
  // repeated Enable taps. Browser caches across SW lifetimes.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }
  const endpoint = sub.endpoint;
  const auth = bufferToBase64(sub.getKey('auth'));
  const p256dh = bufferToBase64(sub.getKey('p256dh'));
  // Send to our backend which forwards to OneSignal's REST API.
  try {
    const res = await api.post('/api/push/subscribe', { endpoint, auth, p256dh });
    return { ok: !!res.data?.ok, response: res.data };
  } catch (e) {
    return { ok: false, reason: 'backend_rejected', detail: e?.response?.data?.message || e?.message };
  }
}

// Silent self-heal (2026-09-02): browsers rotate push endpoints and
// the server retires a subscription after 3 delivery failures (410
// Gone) — after which the device NEVER hears another push, because
// subscribing only ever happened on a manual Settings/nudge tap
// (Ivan's Android went deaf Aug 31 exactly this way: three dead FCM
// rows, zero re-registration). When permission is ALREADY granted,
// this re-registers the live browser subscription on app open —
// idempotent server-side (upsert by endpoint reactivates the row),
// creates a fresh subscription if the browser dropped the old one,
// and never prompts. Once per session.
let ensuredThisSession = false;
export async function ensurePushSubscription() {
  if (ensuredThisSession) return { ok: true, skipped: 'already_ran' };
  ensuredThisSession = true;
  try {
    if (typeof Notification === 'undefined') return { ok: false, reason: 'unsupported' };
    if (Notification.permission !== 'granted') return { ok: false, reason: 'no_permission' };
    if (!('serviceWorker' in navigator)) return { ok: false, reason: 'no_sw' };
    const reg = await registerWorker();
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }
    const res = await api.post('/api/push/subscribe', {
      endpoint: sub.endpoint,
      auth: bufferToBase64(sub.getKey('auth')),
      p256dh: bufferToBase64(sub.getKey('p256dh')),
    });
    return { ok: !!res.data?.ok };
  } catch (e) {
    return { ok: false, detail: e?.message };
  }
}

export async function unsubscribeFromPush() {
  try {
    if (!('serviceWorker' in navigator)) return { ok: true };
    const reg = await navigator.serviceWorker.getRegistration(WORKER_SCOPE);
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) {
      try { await api.post('/api/push/unsubscribe', { endpoint: sub.endpoint }); }
      catch (_) { /* still try local unsub even if backend lookup fails */ }
      await sub.unsubscribe();
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, detail: e?.message };
  }
}

// Returns true if this device currently holds a push subscription for
// the OneSignal SW scope. Used by the Settings card to render the
// correct on/off state.
export async function isPushSubscribed() {
  try {
    if (!('serviceWorker' in navigator)) return false;
    if (typeof Notification === 'undefined') return false;
    if (Notification.permission !== 'granted') return false;
    const reg = await navigator.serviceWorker.getRegistration(WORKER_SCOPE);
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}

export async function sendTestPush() {
  const res = await api.post('/api/push/test', {});
  return res.data;
}
